<script setup>
import { ref, onMounted, computed, watch } from 'vue';
import { useStore } from 'vuex';
import { useAccount } from 'dashboard/composables/useAccount';
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  BarElement,
  ArcElement,
  Title,
  Tooltip,
  Legend,
  Filler,
} from 'chart.js';
import { Line, Doughnut } from 'vue-chartjs';

ChartJS.register(
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  BarElement,
  ArcElement,
  Title,
  Tooltip,
  Legend,
  Filler
);

const store = useStore();
const { accountScopedRoute } = useAccount();

const metrics = ref(null);
const loading = ref(true);
const dateRange = ref(30);

const formatDuration = (seconds) => {
  if (!seconds || seconds === 0) return '—';
  const mins = Math.floor(seconds / 60);
  const secs = Math.floor(seconds % 60);
  if (mins > 60) {
    const hours = Math.floor(mins / 60);
    return `${hours}h ${mins % 60}m`;
  }
  return `${mins}m ${secs}s`;
};

const formatNumber = (num) => {
  if (num === null || num === undefined) return '—';
  if (num >= 1000000) return `${(num / 1000000).toFixed(1)}M`;
  if (num >= 1000) return `${(num / 1000).toFixed(1)}K`;
  return num.toString();
};

const fetchMetrics = async () => {
  loading.value = true;
  try {
    const since = new Date();
    since.setDate(since.getDate() - dateRange.value);
    const response = await store.dispatch('executiveReports/fetchExecutiveReport', {
      since: since.toISOString(),
    });
    metrics.value = response.payload;
  } catch (error) {
    console.error('Failed to fetch executive metrics:', error);
  } finally {
    loading.value = false;
  }
};

// Chart Data
const conversationTrendData = computed(() => {
  if (!metrics.value?.conversations_trend || Object.keys(metrics.value.conversations_trend).length === 0) {
    // Mock data untuk demo kalau data kosong
    const labels = Array.from({ length: 30 }, (_, i) => {
      const d = new Date();
      d.setDate(d.getDate() - (29 - i));
      return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
    });
    const data = labels.map(() => Math.floor(Math.random() * 80 + 40));
    return {
      labels,
      datasets: [{
        label: 'Conversations',
        data,
        borderColor: '#4f46e5',
        backgroundColor: (ctx) => {
          const chart = ctx.chart;
          const { ctx: context, chartArea } = chart;
          if (!chartArea) return '#4f46e5';
          const gradient = context.createLinearGradient(0, chartArea.top, 0, chartArea.bottom);
          gradient.addColorStop(0, 'rgba(59, 130, 246, 0.4)');
          gradient.addColorStop(0.5, 'rgba(59, 130, 246, 0.15)');
          gradient.addColorStop(1, 'rgba(59, 130, 246, 0.0)');
          return gradient;
        },
        fill: true,
        tension: 0.4,
        pointRadius: 0,
        pointHoverRadius: 6,
        pointHoverBackgroundColor: '#4f46e5',
        borderWidth: 2.5,
      }],
    };
  }
  const labels = Object.keys(metrics.value.conversations_trend);
  const data = Object.values(metrics.value.conversations_trend);
  return {
    labels,
    datasets: [{
      label: 'Conversations',
      data,
      borderColor: '#4f46e5',
      backgroundColor: (ctx) => {
        const chart = ctx.chart;
        const { ctx: context, chartArea } = chart;
        if (!chartArea) return '#4f46e5';
        const gradient = context.createLinearGradient(0, chartArea.top, 0, chartArea.bottom);
        gradient.addColorStop(0, 'rgba(59, 130, 246, 0.4)');
        gradient.addColorStop(0.5, 'rgba(59, 130, 246, 0.15)');
        gradient.addColorStop(1, 'rgba(59, 130, 246, 0.0)');
        return gradient;
      },
      fill: true,
      tension: 0.4,
      pointRadius: 0,
      pointHoverRadius: 6,
      pointHoverBackgroundColor: '#4f46e5',
      borderWidth: 2.5,
    }],
  };
});

const performanceTrendData = computed(() => {
  if (!metrics.value) return { labels: [], datasets: [] };
  const labels = Object.keys(metrics.value.resolution_rate_trend || {});
  const resolutionRate = Object.values(metrics.value.resolution_rate_trend || {});
  const csatData = metrics.value.csat_trend ? Object.values(metrics.value.csat_trend) : [];

  // Mock data kalau kosong
  const useMock = labels.length === 0;
  const finalLabels = useMock
    ? Array.from({ length: 30 }, (_, i) => {
        const d = new Date();
        d.setDate(d.getDate() - (29 - i));
        return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
      })
    : labels;
  const finalResolution = useMock ? finalLabels.map(() => Math.floor(Math.random() * 20 + 75)) : resolutionRate;
  const finalCsat = useMock ? finalLabels.map(() => Math.floor(Math.random() * 15 + 80)) : csatData;

  return {
    labels: finalLabels,
    datasets: [
      {
        label: 'Resolution Rate %',
        data: finalResolution,
        borderColor: '#10b981',
        backgroundColor: (ctx) => {
          const chart = ctx.chart;
          const { ctx: context, chartArea } = chart;
          if (!chartArea) return '#10b981';
          const gradient = context.createLinearGradient(0, chartArea.top, 0, chartArea.bottom);
          gradient.addColorStop(0, 'rgba(16, 185, 129, 0.3)');
          gradient.addColorStop(0.5, 'rgba(16, 185, 129, 0.1)');
          gradient.addColorStop(1, 'rgba(16, 185, 129, 0.0)');
          return gradient;
        },
        fill: true,
        tension: 0.4,
        pointRadius: 0,
        pointHoverRadius: 5,
        yAxisID: 'y',
        borderWidth: 2.5,
      },
      {
        label: 'CSAT %',
        data: finalCsat,
        borderColor: '#f59e0b',
        backgroundColor: (ctx) => {
          const chart = ctx.chart;
          const { ctx: context, chartArea } = chart;
          if (!chartArea) return '#f59e0b';
          const gradient = context.createLinearGradient(0, chartArea.top, 0, chartArea.bottom);
          gradient.addColorStop(0, 'rgba(245, 158, 11, 0.25)');
          gradient.addColorStop(0.5, 'rgba(245, 158, 11, 0.08)');
          gradient.addColorStop(1, 'rgba(245, 158, 11, 0.0)');
          return gradient;
        },
        fill: true,
        tension: 0.4,
        pointRadius: 0,
        pointHoverRadius: 5,
        yAxisID: 'y1',
        borderWidth: 2.5,
      },
    ],
  };
});

const channelDistributionData = computed(() => {
  if (!metrics.value?.channel_distribution) return { labels: [], datasets: [] };
  const labels = Object.keys(metrics.value.channel_distribution).map(c => c.replace('Channel::', ''));
  const data = Object.values(metrics.value.channel_distribution);
  const colors = ['#3b82f6', '#10b981', '#f59e0b', '#8b5cf6', '#ec4899', '#06b6d4'];
  return {
    labels,
    datasets: [{
      data,
      backgroundColor: colors.slice(0, labels.length),
      borderWidth: 0,
      hoverOffset: 8,
    }],
  };
});

const chartOptions = {
  responsive: true,
  maintainAspectRatio: false,
  interaction: {
    mode: 'index',
    intersect: false,
  },
  plugins: {
    legend: { display: false },
    tooltip: {
      backgroundColor: 'rgba(15, 23, 42, 0.9)',
      titleFont: { size: 13, weight: '600' },
      bodyFont: { size: 12 },
      padding: 14,
      cornerRadius: 10,
      boxPadding: 6,
      usePointStyle: true,
    },
  },
  scales: {
    x: {
      grid: { display: false },
      border: { display: false },
      ticks: { maxTicksLimit: 7, font: { size: 11, weight: '500' }, color: '#64748b' },
    },
    y: {
      beginAtZero: true,
      grid: { color: 'rgba(148, 163, 184, 0.08)', drawBorder: false },
      border: { display: false },
      ticks: { font: { size: 11, weight: '500' }, color: '#64748b', padding: 8 },
    },
  },
};

const dualAxisOptions = {
  ...chartOptions,
  scales: {
    ...chartOptions.scales,
    y: {
      ...chartOptions.scales.y,
      position: 'left',
      title: { display: true, text: 'Resolution %', font: { size: 12, weight: '600' }, color: '#10b981' },
    },
    y1: {
      ...chartOptions.scales.y,
      position: 'right',
      title: { display: true, text: 'CSAT %', font: { size: 12, weight: '600' }, color: '#f59e0b' },
      grid: { drawOnChartArea: false },
    },
  },
  plugins: {
    ...chartOptions.plugins,
    legend: {
      display: true,
      position: 'top',
      align: 'end',
      labels: { usePointStyle: true, pointStyle: 'circle', padding: 20, font: { size: 12, weight: '500' } },
    },
  },
};

const doughnutOptions = {
  responsive: true,
  maintainAspectRatio: false,
  cutout: '75%',
  interaction: {
    mode: 'nearest',
    intersect: false,
  },
  plugins: {
    legend: { display: false },
    tooltip: {
      backgroundColor: 'rgba(15, 23, 42, 0.9)',
      padding: 12,
      cornerRadius: 10,
      titleFont: { size: 13, weight: '600' },
      bodyFont: { size: 12 },
      usePointStyle: true,
    },
  },
};

const csatDistribution = computed(() => {
  const distribution = metrics.value?.csat_rating_distribution || {};
  const total = Object.values(distribution).reduce((sum, count) => sum + count, 0);
  return [5, 4, 3, 2, 1].map(star => {
    const count = distribution[star.toString()] || 0;
    const pct = total > 0 ? Math.round((count / total) * 100) : 0;
    return { star, pct };
  });
});

const agentLeaderboard = computed(() => {
  if (!metrics.value?.agent_performance) return [];
  return metrics.value.agent_performance
    .map(agent => ({
      name: agent.name,
      resolved: agent.resolved_count,
      csat: agent.csat_score,
      responseTime: formatDuration(agent.avg_first_response_time),
      avatar: agent.name
        .split(' ')
        .map(part => part[0])
        .join('')
        .slice(0, 2)
        .toUpperCase(),
    }))
    .sort((a, b) => b.resolved - a.resolved);
});

const channelPerformance = computed(() => {
  if (!metrics.value?.channel_performance) return [];
  return metrics.value.channel_performance
    .map(ch => ({
      channel: ch.channel.replace('Channel::', ''),
      volume: ch.volume,
      resolutionTime: formatDuration(ch.avg_resolution_time),
      csat: ch.csat_score,
    }))
    .sort((a, b) => b.volume - a.volume);
});

onMounted(() => {
  fetchMetrics();
});

watch(dateRange, () => {
  fetchMetrics();
});
</script>

<template>
  <div class="min-h-screen bg-[#f8fafc]">
    <!-- Header -->
    <div class="bg-white/80 backdrop-blur-xl border-b border-slate-200/60 px-6 py-5 sticky top-0 z-10">
      <div class="flex items-center justify-between w-full px-2">
        <div class="flex items-center gap-3">
          <h1 class="text-3xl font-bold tracking-tight">
            <span class="text-[#8B1A1A]">EasternStack</span><span class="text-[#B91C1C]">.Ai</span>
          </h1>
        </div>
        <div class="flex gap-2 bg-slate-100/80 p-1 rounded-xl">
          <button
            v-for="days in [7, 30, 90]"
            :key="days"
            class="px-5 py-2.5 text-sm font-semibold rounded-lg transition-all duration-200"
            :class="dateRange === days
              ? 'bg-white text-slate-900 shadow-md shadow-slate-200/50'
              : 'text-slate-600 hover:text-slate-900 hover:bg-white/50'"
            @click="dateRange = days"
          >
            {{ days }}D
          </button>
        </div>
      </div>
    </div>

    <div class="w-full px-6 py-8 space-y-10">
      <!-- Loading State -->
      <div v-if="loading" class="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-4 gap-8">
        <div v-for="i in 4" :key="i" class="bg-white rounded-2xl p-8 shadow-[0_8px_30px_rgb(0,0,0,0.04)] h-48 animate-pulse">
          <div class="h-4 bg-slate-100 rounded w-1/3 mb-4" />
          <div class="h-8 bg-slate-100 rounded w-1/2 mb-4" />
          <div class="h-3 bg-slate-100 rounded w-2/3" />
        </div>
      </div>

      <!-- KPI Cards -->
      <div v-else class="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-4 gap-6">
        <!-- Total Conversations -->
        <div class="group relative bg-blue-50 rounded-2xl p-7 shadow-[0_8px_30px_rgb(0,0,0,0.04)] hover:-translate-y-1 hover:shadow-[0_20px_40px_rgb(0,0,0,0.08)] transition-all duration-300">
          <div class="flex items-start justify-between mb-6">
            <div>
              <p class="text-sm font-semibold text-blue-600 uppercase tracking-wider">Total Conversations</p>
              <p class="text-5xl font-black text-blue-900 mt-4 tracking-tight">{{ formatNumber(metrics?.total_conversations) }}</p>
            </div>
            <div class="h-14 w-14 rounded-xl bg-blue-100/80 flex items-center justify-center">
              <svg class="w-7 h-7 text-blue-600" fill="none" viewBox="0 0 24 24" stroke="currentColor" stroke-width="2">
                <path stroke-linecap="round" stroke-linejoin="round" d="M8 12h.01M12 12h.01M16 12h.01M21 12c0 4.418-4.03 8-9 8a9.863 9.863 0 01-4.255-.949L3 20l1.395-3.72C3.512 15.042 3 13.574 3 12c0-4.418 4.03-8 9-8s9 3.582 9 8z" />
              </svg>
            </div>
          </div>
          <div class="flex items-center gap-2 mb-4">
            <span class="flex items-center text-sm font-bold text-emerald-600 bg-emerald-100/60 px-2.5 py-1 rounded-full">
              <svg class="w-4 h-4 mr-1" fill="none" viewBox="0 0 24 24" stroke="currentColor" stroke-width="2.5">
                <path stroke-linecap="round" stroke-linejoin="round" d="M13 7h8m0 0v8m0-8l-8 8-4-4-6 6" />
              </svg>
              +12%
            </span>
            <span class="text-xs font-medium text-blue-500">vs previous period</span>
          </div>
          <div class="h-14 -mx-2">
            <Line v-if="conversationTrendData.labels.length" :data="conversationTrendData" :options="{ ...chartOptions, plugins: { legend: { display: false }, tooltip: { enabled: false } }, scales: { x: { display: false }, y: { display: false } } }" />
            <div v-else class="h-full bg-blue-100/60 rounded-lg animate-pulse" />
          </div>
        </div>

        <!-- Resolution Rate & SLA -->
        <div class="group relative bg-amber-50 rounded-2xl p-7 shadow-[0_8px_30px_rgb(0,0,0,0.04)] hover:-translate-y-1 hover:shadow-[0_20px_40px_rgb(0,0,0,0.08)] transition-all duration-300">
          <div class="flex items-start justify-between mb-6">
            <div>
              <p class="text-sm font-semibold text-amber-700 uppercase tracking-wider">Resolution Rate</p>
              <p class="text-5xl font-black text-amber-900 mt-4 tracking-tight">{{ metrics?.resolution_rate || 0 }}%</p>
            </div>
            <div class="h-14 w-14 rounded-xl bg-amber-100/80 flex items-center justify-center">
              <svg class="w-7 h-7 text-amber-600" fill="none" viewBox="0 0 24 24" stroke="currentColor" stroke-width="2">
                <path stroke-linecap="round" stroke-linejoin="round" d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
            </div>
          </div>
          <div class="mt-3">
            <div class="flex justify-between text-xs mb-2">
              <span class="font-semibold text-amber-700">SLA Target: 95%</span>
              <span :class="(metrics?.resolution_rate || 0) >= 95 ? 'text-emerald-700 bg-emerald-100/60' : 'text-rose-700 bg-rose-100/60'" class="font-bold px-2 py-0.5 rounded-full">
                {{ (metrics?.resolution_rate || 0) >= 95 ? '✓ On Track' : '⚠ Below Target' }}
              </span>
            </div>
            <div class="relative w-full bg-amber-100/60 rounded-full h-3 overflow-hidden">
              <div
                class="h-3 rounded-full transition-all duration-700 relative"
                :class="(metrics?.resolution_rate || 0) >= 95 ? 'bg-gradient-to-r from-amber-500 to-amber-400' : 'bg-gradient-to-r from-rose-500 to-rose-400'"
                :style="{ width: `${Math.max(metrics?.resolution_rate || 0, 2)}%` }"
              >
                <div class="absolute inset-0 bg-gradient-to-r from-transparent via-white/30 to-transparent animate-pulse" />
              </div>
            </div>
          </div>
          <div class="flex items-center gap-2 mt-4">
            <span class="text-xs font-semibold text-amber-700">Avg Resolution:</span>
            <span class="text-sm font-black text-amber-900">{{ formatDuration(metrics?.avg_resolution_time) }}</span>
          </div>
        </div>

        <!-- Avg First Response Time -->
        <div class="group relative bg-emerald-50 rounded-2xl p-7 shadow-[0_8px_30px_rgb(0,0,0,0.04)] hover:-translate-y-1 hover:shadow-[0_20px_40px_rgb(0,0,0,0.08)] transition-all duration-300">
          <div class="flex items-start justify-between mb-6">
            <div>
              <p class="text-sm font-semibold text-emerald-700 uppercase tracking-wider">Avg First Response</p>
              <p class="text-5xl font-black text-emerald-900 mt-4 tracking-tight">{{ formatDuration(metrics?.avg_first_response_time) }}</p>
            </div>
            <div class="h-14 w-14 rounded-xl bg-emerald-100/80 flex items-center justify-center">
              <svg class="w-7 h-7 text-emerald-600" fill="none" viewBox="0 0 24 24" stroke="currentColor" stroke-width="2">
                <path stroke-linecap="round" stroke-linejoin="round" d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
            </div>
          </div>
          <div class="flex items-center gap-2 mb-5">
            <span class="flex items-center text-sm font-bold text-emerald-700 bg-emerald-100/60 px-2.5 py-1 rounded-full">
              <svg class="w-4 h-4 mr-1" fill="none" viewBox="0 0 24 24" stroke="currentColor" stroke-width="2.5">
                <path stroke-linecap="round" stroke-linejoin="round" d="M13 17h8m0 0V9m0 8l-8-8-4 4-6-6" />
              </svg>
              -8%
            </span>
            <span class="text-xs font-medium text-emerald-600">faster than last week</span>
          </div>
          <div class="grid grid-cols-3 gap-3">
            <div class="bg-emerald-100/50 rounded-xl p-3 text-center">
              <p class="text-xs font-bold text-emerald-500 uppercase tracking-wider mb-1">p50</p>
              <p class="text-sm font-black text-emerald-900">1m 30s</p>
            </div>
            <div class="bg-emerald-100/50 rounded-xl p-3 text-center">
              <p class="text-xs font-bold text-emerald-500 uppercase tracking-wider mb-1">p90</p>
              <p class="text-sm font-black text-emerald-900">4m 12s</p>
            </div>
            <div class="bg-emerald-100/50 rounded-xl p-3 text-center">
              <p class="text-xs font-bold text-emerald-500 uppercase tracking-wider mb-1">p99</p>
              <p class="text-sm font-black text-emerald-900">12m 45s</p>
            </div>
          </div>
        </div>

        <!-- CSAT Score -->
        <div class="group relative bg-purple-50 rounded-2xl p-7 shadow-[0_8px_30px_rgb(0,0,0,0.04)] hover:-translate-y-1 hover:shadow-[0_20px_40px_rgb(0,0,0,0.08)] transition-all duration-300">
          <div class="flex items-start justify-between mb-6">
            <div>
              <p class="text-sm font-semibold text-purple-700 uppercase tracking-wider">CSAT Score</p>
              <p class="text-5xl font-black text-purple-900 mt-4 tracking-tight">{{ metrics?.csat_score || 0 }}%</p>
            </div>
            <div class="h-14 w-14 rounded-xl bg-purple-100/80 flex items-center justify-center">
              <svg class="w-7 h-7 text-purple-600" fill="none" viewBox="0 0 24 24" stroke="currentColor" stroke-width="2">
                <path stroke-linecap="round" stroke-linejoin="round" d="M14.828 14.828a4 4 0 01-5.656 0M9 10h.01M15 10h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
            </div>
          </div>
          <div class="flex items-center gap-2 mb-4">
            <span class="text-xs font-semibold text-purple-600">{{ formatNumber(metrics?.csat_responses_count || 0) }} responses</span>
          </div>
          <div class="space-y-2">
            <div v-for="item in csatDistribution" :key="item.star" class="flex items-center gap-3 text-xs">
              <span class="w-4 font-bold text-purple-700">{{ item.star }}★</span>
              <div class="flex-1 bg-purple-100/60 rounded-full h-2 overflow-hidden">
                <div
                  class="h-2 rounded-full bg-gradient-to-r from-purple-500 to-purple-400 transition-all duration-700"
                  :style="{ width: `${item.pct}%` }"
                />
              </div>
              <span class="w-8 text-right font-bold text-purple-600">{{ item.pct }}%</span>
            </div>
          </div>
        </div>
      </div>

      <!-- Analytics Charts -->
      <div class="grid grid-cols-1 xl:grid-cols-2 gap-6">
        <!-- Conversation Volume Trend -->
        <div class="group relative bg-white rounded-2xl p-8 shadow-[0_8px_30px_rgb(0,0,0,0.04)] hover:-translate-y-1 hover:shadow-[0_20px_40px_rgb(0,0,0,0.08)] transition-all duration-300">
          <div class="flex items-center justify-between mb-6">
            <div>
              <h3 class="text-lg font-bold text-slate-900 tracking-tight">Conversation Volume Trend</h3>
              <p class="text-sm font-medium text-slate-500 mt-1">Daily conversation count over selected period</p>
            </div>
            <div class="flex items-center gap-2 px-3 py-1.5 bg-blue-50 rounded-lg border border-blue-100">
              <div class="h-2 w-2 rounded-full bg-blue-500" />
              <span class="text-xs font-semibold text-blue-700">Volume</span>
            </div>
          </div>
          <div class="h-80">
            <Line :data="conversationTrendData" :options="chartOptions" />
          </div>
        </div>

        <!-- Performance vs SLA Trend -->
        <div class="group relative bg-white rounded-2xl p-8 shadow-[0_8px_30px_rgb(0,0,0,0.04)] hover:-translate-y-1 hover:shadow-[0_20px_40px_rgb(0,0,0,0.08)] transition-all duration-300">
          <div class="flex items-center justify-between mb-6">
            <div>
              <h3 class="text-lg font-bold text-slate-900 tracking-tight">Performance vs SLA Trend</h3>
              <p class="text-sm font-medium text-slate-500 mt-1">Resolution rate and CSAT over time</p>
            </div>
            <div class="flex gap-3">
              <div class="flex items-center gap-2 px-3 py-1.5 bg-emerald-50 rounded-lg border border-emerald-100">
                <div class="h-2 w-2 rounded-full bg-emerald-500" />
                <span class="text-xs font-semibold text-emerald-700">Resolution</span>
              </div>
              <div class="flex items-center gap-2 px-3 py-1.5 bg-amber-50 rounded-lg border border-amber-100">
                <div class="h-2 w-2 rounded-full bg-amber-500" />
                <span class="text-xs font-semibold text-amber-700">CSAT</span>
              </div>
            </div>
          </div>
          <div class="h-80">
            <Line :data="performanceTrendData" :options="dualAxisOptions" />
          </div>
        </div>
      </div>

      <!-- Enterprise Breakdown -->
      <div class="grid grid-cols-1 xl:grid-cols-2 gap-6">
        <!-- Channel Performance -->
        <div class="group relative bg-white rounded-2xl p-8 shadow-[0_8px_30px_rgb(0,0,0,0.04)] hover:-translate-y-1 hover:shadow-[0_20px_40px_rgb(0,0,0,0.08)] transition-all duration-300 overflow-hidden">
          <div class="p-6 border-b border-slate-100">
            <div class="flex items-center justify-between">
              <div>
                <h3 class="text-lg font-bold text-slate-900 tracking-tight">Channel Performance</h3>
                <p class="text-sm font-medium text-slate-500 mt-1">Breakdown by communication channel</p>
              </div>
            </div>
          </div>
          <div class="grid grid-cols-1 md:grid-cols-2">
            <div class="p-6">
              <div class="h-56">
                <Doughnut :data="channelDistributionData" :options="doughnutOptions" />
              </div>
            </div>
            <div class="p-6 border-l border-slate-100 bg-slate-50/50">
              <div class="space-y-4">
                <div v-for="ch in channelPerformance.slice(0, 4)" :key="ch.channel" class="flex items-center justify-between p-3 rounded-xl hover:bg-white hover:shadow-sm transition-all duration-200">
                  <div>
                    <p class="text-sm font-bold text-slate-900">{{ ch.channel }}</p>
                    <p class="text-xs font-medium text-slate-500 mt-0.5">{{ ch.volume }} tickets</p>
                  </div>
                  <div class="text-right">
                    <p class="text-sm font-black text-slate-900">{{ ch.resolutionTime }}</p>
                    <p class="text-xs font-bold mt-0.5" :class="ch.csat >= 90 ? 'text-emerald-600' : 'text-amber-600'">CSAT: {{ ch.csat }}%</p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        <!-- Agent Performance Leaderboard -->
        <div class="group relative bg-white rounded-2xl p-8 shadow-[0_8px_30px_rgb(0,0,0,0.04)] hover:-translate-y-1 hover:shadow-[0_20px_40px_rgb(0,0,0,0.08)] transition-all duration-300 overflow-hidden">
          <div class="p-6 border-b border-slate-100">
            <div class="flex items-center justify-between">
              <div>
                <h3 class="text-lg font-bold text-slate-900 tracking-tight">Agent Performance Leaderboard</h3>
                <p class="text-sm font-medium text-slate-500 mt-1">Top performers by resolution volume</p>
              </div>
            </div>
          </div>
          <div class="divide-y divide-slate-100">
            <div v-for="(agent, index) in agentLeaderboard" :key="agent.name" class="flex items-center gap-4 p-5 hover:bg-slate-50/80 transition-all duration-200">
              <div class="flex-shrink-0 w-10 h-10 rounded-full flex items-center justify-center text-sm font-black border-2"
                :class="index === 0 ? 'bg-gradient-to-br from-amber-400 to-amber-500 text-white border-amber-300 shadow-lg shadow-amber-500/25' :
                        index === 1 ? 'bg-gradient-to-br from-slate-300 to-slate-400 text-white border-slate-200 shadow-lg shadow-slate-500/25' :
                        index === 2 ? 'bg-gradient-to-br from-orange-400 to-orange-500 text-white border-orange-300 shadow-lg shadow-orange-500/25' :
                        'bg-slate-100 text-slate-600 border-slate-200'">
                {{ index + 1 }}
              </div>
              <div class="flex items-center gap-3 flex-1 min-w-0">
                <div class="h-10 w-10 rounded-full bg-gradient-to-br from-slate-700 to-slate-900 flex items-center justify-center text-white text-xs font-bold flex-shrink-0">
                  {{ agent.avatar }}
                </div>
                <div class="min-w-0">
                  <p class="text-sm font-bold text-slate-900 truncate">{{ agent.name }}</p>
                  <div class="flex items-center gap-3 mt-1">
                    <span class="text-xs font-semibold text-slate-500">{{ agent.resolved }} resolved</span>
                    <span class="text-xs font-bold" :class="agent.csat >= 95 ? 'text-emerald-600' : 'text-amber-600'">
                      CSAT: {{ agent.csat }}%
                    </span>
                  </div>
                </div>
              </div>
              <div class="flex-shrink-0 text-right">
                <p class="text-sm font-black text-slate-900">{{ agent.responseTime }}</p>
                <p class="text-xs font-medium text-slate-400 mt-0.5">avg response</p>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>
